'use client';

import { useMemo } from 'react';
import GfInteractiveMap from './GfInteractiveMap';
import { t } from '@/i18n/translations';

const cityTitle = (c) => c?.localizedName || c?.label || c?.name || '';
const poiTitle = (a) => a?.page?.h1 || a?.nameEn || a?.localName || '';

/**
 * Карта страны: listed-слой = города (нумерованные + список), плюс слои-точки
 * «Достопримечательности» и «Аэропорты» с переключателями видимости в легенде.
 * Тонкий адаптер над переиспользуемым GfInteractiveMap.
 */
export default function CountryInteractiveMap({
    cities = [],
    attractions = [],
    airports = [],
    lang = 'ru',
    countrySlug = '',
    zoom = 7,
}) {
    const ui = t(lang, 'mapsUi').country;

    const cityPoints = useMemo(
        () => (cities || [])
            .filter((c) => c?.coords?.lat && c?.coords?.lng)
            .map((c) => {
                const slug = c.slug?.current || c.slug;
                return {
                    id: c._id || slug,
                    title: cityTitle(c),
                    subtitle: c.subtitle || '',
                    coords: c.coords,
                    cover: c.cover,
                    href: slug && countrySlug ? `/${lang}/${countrySlug}/${slug}` : null,
                    meta: null,
                };
            }),
        [cities, lang, countrySlug]
    );

    const poiPoints = useMemo(
        () => (attractions || [])
            .filter((a) => a?.coords?.lat && a?.coords?.lng)
            .map((a) => {
                const slug = a.page?.poiSlug || a.slug?.current || (typeof a.slug === 'string' ? a.slug : null);
                const citySlug = a.citySlug;
                return {
                    id: a._id || slug,
                    title: poiTitle(a),
                    subtitle: a.page?.subtitle || '',
                    coords: a.coords,
                    cover: a.cover,
                    href: slug && citySlug && countrySlug ? `/${lang}/${countrySlug}/${citySlug}/places/${slug}` : null,
                    meta: a.cityName || null,
                };
            }),
        [attractions, lang, countrySlug]
    );

    const airportPoints = useMemo(
        () => (airports || [])
            .filter((a) => a?.coords?.lat && a?.coords?.lng)
            .map((a) => ({
                id: a._id || a.iata,
                title: a.title || a.name || '',
                subtitle: '',
                coords: a.coords,
                cover: null,
                href: a.iata && countrySlug ? `/${lang}/${countrySlug}/airport/${String(a.iata).toLowerCase()}` : null,
                meta: a.iata || null,
            })),
        [airports, lang, countrySlug]
    );

    const layers = useMemo(() => [
        { key: 'city', kind: 'city', listed: true, swatch: 'var(--pine)', label: ui.cities, points: cityPoints },
        { key: 'poi', kind: 'poi', toggleable: true, swatch: 'var(--stamp)', kicker: ui.placesKicker, label: ui.places, points: poiPoints },
        { key: 'airport', kind: 'airport', toggleable: true, swatch: 'var(--ink)', kicker: ui.airportKicker, label: ui.airports, points: airportPoints },
    ], [cityPoints, poiPoints, airportPoints, ui]);

    return (
        <GfInteractiveMap
            layers={layers}
            center={null}
            panelEyebrow={ui.panelEyebrow}
            hint={ui.hint}
            ariaLabel={ui.aria}
            lang={lang}
            zoom={zoom}
            fitMaxZoom={10}
        />
    );
}
